import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LangToggle } from "@/components/LangToggle";
import { fileUrl, clearShopToken, getShopSession } from "@/lib/api";
import { Building2, LogOut, Home, ClipboardEdit, LineChart, Settings2, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { MyProfilePopover } from "@/components/MyProfilePopover";

function NavItem({ to, active, icon: Icon, label, testId, onClick }) {
  const base = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition";
  const cls = active
    ? "bg-emerald-800 text-amber-50 shadow-sm"
    : "text-stone-600 hover:text-stone-900 hover:bg-stone-100";
  const inner = <><Icon className="w-3.5 h-3.5" />{label}</>;
  return (
    <Link to={to} onClick={onClick} className={`${base} ${cls}`} data-testid={testId}>
      {inner}
    </Link>
  );
}

export function ShopHeader({ shop, showSignOut = false }) {
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useLang();
  const session = shop ? getShopSession(shop.slug) : null;
  const role = session?.role;
  const isManager = role === "owner" || role === "manager";
  const slug = shop?.slug || "";
  const path = loc.pathname.replace(/\/+$/, "");
  const homePath = `/${slug}`;
  const inputPath = `/${slug}/input`;
  const reportsPath = `/${slug}/reports`;
  const kpiPath = `/${slug}/kpi`;
  const adminPath = `/${slug}/admin`;

  return (
    <header className="bg-white/80 backdrop-blur border-b border-stone-200 sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
        <Link to={homePath} className="flex items-center gap-3" data-testid="shop-logo-link">
          {shop?.logo_path ? (
            <img src={fileUrl(shop.logo_path)} alt={shop.name} className="w-10 h-10 rounded object-cover ring-1 ring-stone-200" />
          ) : (
            <div className="w-10 h-10 rounded bg-gradient-to-br from-emerald-800 to-emerald-950 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-200" />
            </div>
          )}
          <div className="leading-tight">
            <div className="font-serif text-lg text-stone-900">{shop?.name || "Shop"}</div>
            <div className="caption-label mt-0.5">Management Information System</div>
          </div>
        </Link>

        {shop && session?.token && (
          <nav
            className="order-3 lg:order-2 w-full lg:w-auto flex items-center gap-1 overflow-x-auto -mx-1 px-1 lg:mx-0 lg:px-0"
            data-testid="shop-nav-menu"
          >
            <NavItem to={homePath} active={path === homePath} icon={Home} label={t("client.home")} testId="nav-home" />
            <NavItem to={inputPath} active={path === inputPath} icon={ClipboardEdit} label={t("client.input")} testId="nav-input" />
            {isManager && (
              <NavItem to={reportsPath} active={path === reportsPath} icon={LineChart} label={t("client.reports")} testId="nav-reports" />
            )}
            {isManager && (
              <NavItem to={kpiPath} active={path === kpiPath} icon={Gauge} label={t("client.kpi")} testId="nav-kpi" />
            )}
            {isManager && (
              <NavItem to={adminPath} active={path === adminPath} icon={Settings2} label={t("client.administration")} testId="nav-admin" />
            )}
          </nav>
        )}

        <div className="order-2 lg:order-3 flex items-center gap-3 ml-auto lg:ml-0">
          {session?.person && (
            isManager ? (
              <MyProfilePopover slug={shop.slug} role={session.role} personName={session.person} />
            ) : (
              <span
                className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 px-2.5 py-1 text-xs font-mono text-emerald-900"
                data-testid="shop-session-person"
              >
                <span className="uppercase tracking-wider text-[10px] text-emerald-700">{session.role}</span>
                <span className="text-stone-400">·</span>
                <span>{session.person}</span>
              </span>
            )
          )}
          <LangToggle variant="light" />
          {showSignOut && shop && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { clearShopToken(shop.slug); nav(`/${shop.slug}`); }}
              className="text-stone-700"
              data-testid="shop-signout-button"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" /> {t("common.sign_out")}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
